import { useState } from "react";
import { ACTIVITY_KINDS, ACTIVITY_KIND_LABEL, LOCATION_LABEL, UNITS, fmt, pct, type ActivityKind, type CalcResult, type Equipment, type Scenario } from "@h2lca/core";
import { Button, Select, SelectField, TextField } from "@/components/ui/FormControls";
import { templateById, useMaster } from "@/store/master";
import { useWorkshop } from "@/store/workshop";
import { StageBadge } from "../Badges";
import { IssueLine } from "../IssueList";
import { NumField } from "../NumField";
import { InputCard } from "./InputCard";

const ROLE_LABEL: Record<Equipment["h2"]["role"], string> = { none: "수소가 지나지 않음", producer: "수소 생산", passthrough: "수소 통과(투입→산출)" };

/** 선택한 설비 하나의 운전자료 , 수소 흐름과 활동자료 항목 */
export function EquipmentInputsEditor({ scenario, result, eq }: { scenario: Scenario; result: CalcResult; eq: Equipment }) {
  const data = useMaster((s) => s.data);
  const updateEquipment = useWorkshop((s) => s.updateEquipment);
  const setH2 = useWorkshop((s) => s.setH2);
  const rechain = useWorkshop((s) => s.rechain);
  const addInput = useWorkshop((s) => s.addInput);
  const tpl = templateById(data, eq.templateId);
  const r = result.byEquipment.find((x) => x.equipmentId === eq.id);
  const isCustom = eq.templateId === "custom";
  const kgThrough = eq.h2.role === "passthrough" ? (eq.h2.inKg ?? scenario.basis.batchH2Kg) : scenario.basis.batchH2Kg;
  const eqIssues = result.issues.filter((i) => i.equipmentId === eq.id && !i.inputId);
  const lossRate = eq.h2.inKg && eq.h2.lossKg !== null ? eq.h2.lossKg / eq.h2.inKg : null;
  const massError = eqIssues.some((i) => i.code === "MASS_BALANCE" && i.level === "error");

  const [nLabel, setNLabel] = useState("");
  const [nKind, setNKind] = useState<ActivityKind>("electricity");
  const [nUnit, setNUnit] = useState("kWh");

  return (
    <div className="flex flex-col gap-content">
      <div className="flex flex-wrap items-end gap-item">
        <div className="ws-equipment-name">
          <TextField label={<span className="inline-flex items-center gap-inline"><StageBadge stage={eq.stage} /> 설비명</span>} value={eq.name} onChange={(e) => updateEquipment(eq.id, { name: e.target.value })} />
        </div>
        <p className="text-footnote text-ink-subtle sm:pb-item">
          {LOCATION_LABEL[eq.location]}, {r?.inBoundary ? "경계 안" : "경계 밖(계산 제외)"}, 배출량 <b className="font-mono text-ink">{r ? fmt(r.kgCO2eq) : "없음"}</b> kgCO2eq
        </p>
      </div>
      {eqIssues.length > 0 && (
        <div className="flex flex-col gap-label">
          {eqIssues.map((i, k) => (
            <IssueLine key={`${i.code}-${k}`} issue={i} link={false} />
          ))}
        </div>
      )}
      {tpl?.help && <p className="text-footnote text-ink-subtle">{tpl.help}</p>}

      <div className="rounded-card border border-line-soft bg-surface-inset p-content">
        <div className="mb-item flex flex-wrap items-center justify-between gap-item">
          <h4 className="text-caption font-semibold text-ink">수소 흐름</h4>
          <div className="flex flex-wrap items-center gap-item">
            {isCustom && (
              <Select value={eq.h2.role} onChange={(e) => setH2(eq.id, { role: e.target.value as Equipment["h2"]["role"], inKg: null, outKg: e.target.value === "none" ? null : kgThrough, lossKg: e.target.value === "passthrough" ? 0 : null })}>
                {(Object.keys(ROLE_LABEL) as Equipment["h2"]["role"][]).map((k) => (
                  <option key={k} value={k}>
                    {ROLE_LABEL[k]}
                  </option>
                ))}
              </Select>
            )}
            <Button variant="secondary" onClick={rechain} title="앞 설비의 산출량을 다음 설비의 투입량으로 다시 채웁니다(손실률 유지)">
              수소 흐름 다시 잇기
            </Button>
          </div>
        </div>
        {eq.h2.role === "none" && <p className="text-footnote text-ink-subtle">{ROLE_LABEL.none}. 보조 및 원료 공급 설비입니다.</p>}
        {eq.h2.role === "producer" && (
          <div className="ws-form-grid">
            <NumField label="수소 생산량" unit="kg" value={eq.h2.outKg} min={0} onChange={(v) => setH2(eq.id, { outKg: v })} hint={`산정 기준량 ${fmt(scenario.basis.batchH2Kg)} kg 을 만드는 설비면 같은 값`} />
          </div>
        )}
        {eq.h2.role === "passthrough" && (
          <div className="input-card-grid">
            <NumField label="투입 수소" unit="kg" value={eq.h2.inKg} min={0} onChange={(v) => setH2(eq.id, { inKg: v })} />
            <NumField label="손실" unit="kg" value={eq.h2.lossKg} min={0} onChange={(v) => setH2(eq.id, { lossKg: v, outKg: v !== null && eq.h2.inKg !== null ? Math.max(0, eq.h2.inKg - v) : eq.h2.outKg })} />
            <NumField label="산출 수소" unit="kg" value={eq.h2.outKg} min={0} onChange={(v) => setH2(eq.id, { outKg: v })} error={massError ? "투입·산출·손실 수소량의 물질수지를 확인하세요" : undefined} />
            <NumField
              label="손실률"
              unit="%"
              value={lossRate === null ? null : Math.round(lossRate * 10000) / 100}
              min={0}
              disabled={eq.h2.inKg === null}
              onChange={(v) => {
                if (v === null || eq.h2.inKg === null) return;
                const loss = eq.h2.inKg * Math.min(1, Math.max(0, v / 100));
                setH2(eq.id, { lossKg: loss, outKg: eq.h2.inKg - loss });
              }}
              hint="손실 = 투입 × 손실률"
            />
          </div>
        )}
        {eq.h2.role === "passthrough" && eq.h2.inKg !== null && eq.h2.outKg !== null && eq.h2.lossKg !== null && (
          <p className="mt-label font-mono text-footnote text-ink-muted">
            투입 {fmt(eq.h2.inKg)} kg / 산출 {fmt(eq.h2.outKg)} kg / 손실 {fmt(eq.h2.lossKg)} kg{lossRate !== null && ` (손실률 ${pct(lossRate, 2)})`}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-item">
        <h4 className="text-caption font-semibold text-ink">활동자료 (산정 기준량 {fmt(scenario.basis.batchH2Kg)} kg 기준 사용량)</h4>
        {eq.inputs.length === 0 && <p className="text-footnote text-ink-subtle">입력 항목이 없습니다. 아래에서 항목을 추가하세요.</p>}
        {eq.inputs.map((input) => (
          <InputCard
            key={input.id}
            eq={eq}
            input={input}
            line={r?.lines.find((l) => l.inputId === input.id)}
            tplInput={tpl?.inputs.find((t) => t.key === input.key)}
            kgThrough={kgThrough}
            removable={isCustom || !tpl?.inputs.some((t) => t.key === input.key)}
            issues={result.issues.filter((i) => i.equipmentId === eq.id && i.inputId === input.id)}
          />
        ))}
      </div>

      <form
        className="ws-input-add"
        onSubmit={(e) => {
          e.preventDefault();
          const label = nLabel.trim() || ACTIVITY_KIND_LABEL[nKind];
          addInput(eq.id, { key: `custom_${Date.now().toString(36)}`, label, kind: nKind, amount: null, unit: nUnit, valueType: "missing", required: true });
          setNLabel("");
        }}
      >
        <TextField label="새 항목명" value={nLabel} onChange={(e) => setNLabel(e.target.value)} placeholder="예: 스팀 사용량" />
        <SelectField label="활동자료 종류" value={nKind} onChange={(e) => setNKind(e.target.value as ActivityKind)}>
          {ACTIVITY_KINDS.map((k) => (
            <option key={k} value={k}>
              {ACTIVITY_KIND_LABEL[k]}
            </option>
          ))}
        </SelectField>
        <SelectField label="단위" value={nUnit} onChange={(e) => setNUnit(e.target.value)}>
          {Object.values(UNITS).map((u) => (
            <option key={u.symbol} value={u.symbol}>
              {u.symbol}, {u.label}
            </option>
          ))}
        </SelectField>
        <Button type="submit" variant="soft">
          항목 추가
        </Button>
      </form>
    </div>
  );
}
